/**
 * <TutorSessionFeed> — la colonne de droite du live du masteur : **la session de l'élève regardé avec le
 * champion** (organism).
 *
 * Wajih a choisi la piste C le 02/11 : « C'est plus cohérent que ce soit centré sur l'élève actuel, et
 * qu'on puisse voir "ailleurs" en haut. » La colonne est une session par élève, nommée en tête
 * (la tête du champion et « <champion> pour Léa »), et dite dans une grammaire de quatre formes
 * (`<TutorSessionEntry>`) : le texte du champion sans carte, une trace datée, la bulle du masteur à
 * droite, et une carte réservée à ce qui attend une décision.
 *
 * **Le titre est le filtre** (round 4, Wajih : le titre et le filtre sur Léa sont redondants, « il faut
 * fusionner ») : filtré, « <champion> pour Léa » est une puce qu'on retire d'un geste ; retirée, le titre
 * dit « <champion> pour tout le groupe », et « Seulement Léa » y ramène.
 *
 * **Une suggestion ne se perd jamais.** Ce qui attend une décision tient dans le bloc du bas, juste
 * au-dessus du champ : « À décider » d'abord, « Ailleurs » ensuite, en onglets quand les deux ont
 * quelque chose, sans onglets quand un seul en a. Sa naissance, sa péremption (elle sort seule, avec sa
 * raison, ou passé sa durée de vie) et sa décision laissent chacune une trace dans l'historique
 * (`tutorSessionTimeline`, pur et testé). Chez un autre élève : le compte sur sa vignette et dans
 * « Ailleurs ». Au retour sur un élève : le bas de l'historique, le bloc, et le repère « Depuis ton
 * dernier passage ».
 *
 * **La colonne garde sa largeur** (Wajih, 04/11 : « ni l'agrandir ni la rétrécir ») : « Agrandir » a disparu. Ce qui
 * se relit et se décide monte au centre (`<TutorReviewStage>`, posé par la page) ; la colonne pilote : la ligne
 * vivante, la carte « prêtes », « <champion> propose ». **Le ruban de la séance** (`ribbon`) se pose sous l'en-tête.
 *
 * **Générer dans la session** (`composer`, `<TutorDraftComposer>`) : le texte du champ devient les instructions, les
 * réglages pré-remplis au-dessus passent à la ligne et se règlent sur place, la pièce d'où part la génération (une
 * image, une photo du chat, « similaire ») est en tête. Aucune donnée nouvelle : c'est le brouillon du formulaire
 * d'aujourd'hui (`activityDraftSettings`, `activityDraftOptions`).
 *
 * Sans élève regardé (la mosaïque) : le groupe, un élève par ligne avec ce qu'il attend, et rien à écrire.
 * Ancrée à partir de 1 024 px, en tiroir en dessous (sa croix), plein écran au téléphone : les mêmes formes.
 */
import React, { useEffect, useRef, useState } from "react"
import { ScrollView, View, type NativeScrollEvent, type NativeSyntheticEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { endSentence } from "../../../ports/primary/ui/formatters/textFormatter"
import { tutorPendingLabel } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import {
    type UITutorFeedItem,
    type UITutorSession,
    type UITutorSessionElsewhere,
} from "../../../ports/primary/ui/types/tutorLive"
import { Avatar } from "../../atoms/Avatar"
import { CHAMPIONS, ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Badge } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { type IconName } from "../../atoms/icons/Icon"
import { Divider } from "../../atoms/Divider"
import { Tabs } from "../../molecules/Tabs"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AuroraFill } from "../../molecules/AuroraFill"
import { EmptyState } from "../../molecules/EmptyState"
import { TutorDraftComposer, type TutorSessionComposer } from "../../molecules/TutorDraftComposer"
import { TutorSessionEntry, type TutorSessionEntryProps } from "../TutorSessionEntry"
import { TutorSessionFeedCard } from "../../molecules/TutorSessionFeedCard"
import { cappedCount } from "../../utils/cappedCount"
import { dsScrollProps } from "../../utils/webScrollbars"

export type { TutorSessionComposer, UITutorComposerMode } from "../../molecules/TutorDraftComposer"

export type TutorSessionFeedProps = {
    /** Le champion qui aide en séance : sa tête et son prénom signent ses lignes. */
    helper: ChampionName
    /** La session de l'élève regardé. Absente : le groupe (la mosaïque, ou personne de regardé). */
    session?: UITutorSession | null
    /** Les autres élèves qui attendent le masteur (`tutorSessionElsewhere`, sans l'élève regardé). */
    elsewhere: UITutorSessionElsewhere[]
    /**
     * Le filtre sur l'élève regardé (round 4 : « un filtre sur cet élève, et que ce soit visible que c'est bien
     * un filtre ; je dois pouvoir l'enlever »), porté par le titre : la puce « <champion> pour Léa × » ; retirée, le
     * fil de tout le groupe et « Seulement Léa » pour y revenir. Absent (la mosaïque, personne de regardé) :
     * le titre seul. Filtré : la session est celle de l'élève (`session.student`).
     */
    filter?: { firstname: string; onToggle: () => void }
    /** Regarder un autre élève : sa session prend la colonne. */
    onSelectStudent: (studentId: string) => void
    /** Une carte du bac : son action, ou l'écarter. */
    onAction: (itemId: string, actionId: string) => void
    onDismiss: (itemId: string) => void
    /** L'action qui part, sur sa carte. */
    pending?: { itemId: string; actionId: string }
    /** Une activité générée : l'ajouter, la montrer à l'élève, la régénérer. */
    onResult?: TutorSessionEntryProps["onResult"]
    /** Un geste d'une ligne de la génération : « Revoir », envoyer, « <champion> propose ». */
    onGeneration?: TutorSessionEntryProps["onGeneration"]
    /** Une proposition survolée (web) : son aperçu au centre. */
    onPeek?: TutorSessionEntryProps["onPeek"]
    peekId?: string | null
    composer?: TutorSessionComposer
    /** Le ruban de la séance de l'élève regardé, sous l'en-tête. Absent : rien. */
    ribbon?: React.ReactNode
    /** En tiroir (sous 1 024 px) : sa croix. Absent : la colonne ancrée. */
    onClose?: () => void
    /** Replier la colonne (le chevron de l'en-tête). Absent : pas de chevron. */
    onCollapse?: () => void
    testID?: string
}

const TEXTS = {
    all: "Tous",
    allLabel: (name: string) => `${name} pour tout le groupe`,
    forLabel: (name: string, firstname: string) => `${name} pour ${firstname} seulement`,
    helperActive: (firstname: string) => `en conversation avec ${firstname}`,
    elsewhere: "Ailleurs",
    look: (firstname: string) => `Voir ${firstname}`,
    tray: "À décider",
    block: "À décider et ailleurs",
    emptyClass: "Rien encore pour le groupe.",
    trayLess: "Réduire",
    trayMore: (count: number) => (count === 1 ? "Voir l'autre" : `Voir les ${count} autres`),
    empty: (firstname: string) => endSentence(`Rien encore avec ${firstname}`),
    startedHere: (at: string) => `Sur cet appareil depuis ${at}`,
    startedHereNote: (name: string) =>
        `Le reste de l'historique est resté sur le navigateur où il a commencé. Les résumés de ${name} et les rendus à valider sont là.`,
    close: "Fermer la session",
    collapse: "Replier la colonne",
    calmTitle: "Tout va bien",
    calmMessage: "Personne n'attend de décision.",
}

/** Le bac montre sa première carte, la plus pressante : au-delà, il prendrait l'historique. */
const TRAY_SHOWN = 1
/** « Ailleurs » montre ses deux premières lignes, les urgences d'abord. */
const ELSEWHERE_SHOWN = 2
/** La clé du fil sans filtre : le groupe. */
const CLASS_KEY = "__class__"

type BlockTab = "elsewhere" | "tray"
/** Déplié, le bac défile au-delà de cette hauteur : l'historique garde sa place au-dessus. */
const TRAY_OPEN_MAX = 280
/** À quelques pixels du bas, on y est encore : l'historique suit ce qui arrive. */
const BOTTOM_SLACK = 24
/** La tête du champion dans l'en-tête de la colonne. */
const HEAD_SIZE = 28

export function TutorSessionFeed(props: TutorSessionFeedProps) {
    const {
        helper,
        session,
        elsewhere,
        onSelectStudent,
        filter,
        onAction,
        onDismiss,
        pending,
        onResult,
        composer,
        onGeneration,
        onPeek,
        peekId,
        ribbon,
        onClose,
        onCollapse,
        testID,
    } = props
    const helperName = CHAMPIONS[helper].label
    // La clé de ce que montre le fil : l'élève filtré, ou le groupe.
    const studentId = session != null ? (session.student?.id ?? CLASS_KEY) : undefined
    const scrollRef = useRef<ScrollView>(null)
    // L'historique se place UNE fois par élève : au repère « Depuis ton dernier passage » au retour, en haut
    // sur un autre appareil (sa phrase comprise), sinon au bas. Ensuite il ne suit le bas que si on y était :
    // déplier un paragraphe plus haut ne fait rien sauter.
    const placedFor = useRef<string | null>(null)
    const atBottom = useRef(true)
    const since = useRef<{ studentId: string; y: number } | null>(null)
    const hasSince = session?.history.some((entry) => entry.kind === "since") === true
    const settle = () => {
        const scroll = scrollRef.current
        if (scroll == null || studentId == null) return
        if (placedFor.current === studentId) {
            if (atBottom.current) followEnd()
            return
        }
        if (session?.startedHere != null) scroll.scrollTo({ y: 0, animated: false })
        else if (hasSince) {
            if (since.current?.studentId !== studentId) return
            scroll.scrollTo({ y: since.current.y, animated: false })
        } else scroll.scrollToEnd({ animated: false })
        // Placé ailleurs qu'au bas, il ne suit pas ce qui arrive avant que le lecteur y descende.
        atBottom.current = session?.startedHere == null && !hasSince
        placedFor.current = studentId
    }
    // Android s'arrête parfois à l'ancien bas : on y revient deux images plus tard (`<ConversationThread>`).
    const frame = useRef<number | null>(null)
    const followEnd = () => {
        scrollRef.current?.scrollToEnd({ animated: false })
        if (frame.current != null) cancelAnimationFrame(frame.current)
        frame.current = requestAnimationFrame(() => {
            frame.current = requestAnimationFrame(() => {
                frame.current = null
                if (atBottom.current) scrollRef.current?.scrollToEnd({ animated: false })
            })
        })
    }
    useEffect(
        () => () => {
            if (frame.current != null) cancelAnimationFrame(frame.current)
        },
        []
    )
    const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent
        atBottom.current = contentOffset.y + layoutMeasurement.height >= contentSize.height - BOTTOM_SLACK
    }
    // État d'interface : l'onglet du bloc et l'onglet déplié, pour ce que montre le fil ; ils reviennent à
    // « À décider », replié, quand on en change. Déplier un onglet ne déplie pas l'autre.
    const [block, setBlock] = useState<{ key: string; tab?: BlockTab; open?: BlockTab } | null>(null)
    const blockState = block != null && block.key === studentId ? block : { key: studentId ?? CLASS_KEY }

    const card = (item: UITutorFeedItem) => (
        <TutorSessionFeedCard
            key={item.id}
            item={item}
            onAction={(actionId) => onAction(item.id, actionId)}
            onDismiss={() => onDismiss(item.id)}
            pendingActionId={pending?.itemId === item.id ? pending.actionId : undefined}
            helper={helper}
        />
    )

    const elsewhereRow = (row: UITutorSessionElsewhere) => (
        <Row key={row.student.id} gap="2" align="center">
            <Avatar uri={row.student.avatarUrl} name={row.student.firstname} size={20} />
            <Text size="text-sm" color="text-primary" numberOfLines={1} style={styles.flex}>
                <Text size="text-sm" weight="bold" color="text-strong">
                    {row.student.firstname}
                </Text>{" "}
                {row.urgent != null ? row.urgent.text : tutorPendingLabel(row.pending)}
            </Text>
            {row.urgent != null && row.pending > 0 && <Badge text={cappedCount(row.pending)} variant="neutral" />}
            <TextButton
                label={TEXTS.look(row.student.firstname)}
                size="sm"
                onPress={() => onSelectStudent(row.student.id)}
            />
        </Row>
    )

    const head = (
        <Row gap="2" align="center" style={styles.head}>
            {session?.helperActive === true && <AuroraFill radius={0} />}
            <ChampionAvatar name={helper} size={HEAD_SIZE} />
            <Stack gap="0" style={styles.flex}>
                <Row gap="1" align="center" style={styles.wrapRow}>
                    <ChoiceChip
                        label={TEXTS.all}
                        selected={filter == null || session?.student == null}
                        onPress={() => {
                            if (filter != null && session?.student != null) filter.onToggle()
                        }}
                        accessibilityLabel={TEXTS.allLabel(helperName)}
                    />
                    {filter != null && (
                        <ChoiceChip
                            label={filter.firstname}
                            selected={session?.student != null}
                            onPress={() => {
                                if (session?.student == null) filter.onToggle()
                            }}
                            accessibilityLabel={TEXTS.forLabel(helperName, filter.firstname)}
                        />
                    )}
                </Row>
                {session?.helperActive === true && session.student != null && (
                    <Text size="text-xs" color="text-muted" numberOfLines={1}>
                        {TEXTS.helperActive(session.student.firstname)}
                    </Text>
                )}
            </Stack>
            {onCollapse != null && (
                <HeadButton iconName="panel-right-close" onPress={onCollapse} label={TEXTS.collapse} />
            )}
            {onClose != null && <HeadButton iconName="times" iconSize={18} onPress={onClose} label={TEXTS.close} />}
        </Row>
    )

    if (session == null) {
        return (
            <View style={styles.root} testID={testID}>
                {head}
                <ScrollView contentContainerStyle={styles.scroll} {...dsScrollProps()}>
                    {elsewhere.length === 0 ? (
                        <EmptyState title={TEXTS.calmTitle} message={TEXTS.calmMessage} iconName="check-circle" />
                    ) : (
                        <Stack gap="3">{elsewhere.map(elsewhereRow)}</Stack>
                    )}
                </ScrollView>
            </View>
        )
    }

    // Le bloc du bas : « À décider » et « Ailleurs » en onglets, un seul bloc ; « À décider » d'abord (Wajih,
    // round 4 : « À décider doit être le premier onglet, Ailleurs le deuxième »). Sans onglets quand un seul a
    // quelque chose.
    const urgentElsewhere = elsewhere.some((row) => row.urgent != null)
    const tabs = [
        {
            key: "tray" as const,
            label: TEXTS.tray,
            count: session.tray.length,
            urgent: false,
            shown: TRAY_SHOWN,
            render: (count: number) => session.tray.slice(0, count).map(card),
        },
        {
            key: "elsewhere" as const,
            label: TEXTS.elsewhere,
            count: elsewhere.length,
            urgent: urgentElsewhere,
            shown: ELSEWHERE_SHOWN,
            render: (count: number) => <Stack gap="1">{elsewhere.slice(0, count).map(elsewhereRow)}</Stack>,
        },
    ].filter((tab) => tab.count > 0)
    const active = tabs.find((tab) => tab.key === blockState.tab) ?? tabs[0]
    const blockOpen = active != null && blockState.open === active.key
    const hidden = active == null || blockOpen ? 0 : Math.max(0, active.count - active.shown)
    const blockItems = active?.render(blockOpen ? active.count : active.shown)

    return (
        <View style={styles.root} testID={testID}>
            {head}
            {ribbon != null && <View style={styles.ribbon}>{ribbon}</View>}
            <View style={styles.column}>
                <ScrollView
                    ref={scrollRef}
                    {...dsScrollProps()}
                    // Clavier du brouillon ouvert, un appui sur une carte du fil agit du premier coup.
                    keyboardShouldPersistTaps="handled"
                    style={styles.flexFill}
                    contentContainerStyle={styles.scroll}
                    onContentSizeChange={settle}
                    onScroll={onScroll}
                    scrollEventThrottle={100}
                >
                    <Stack gap="3">
                        {session.startedHere != null && (
                            <Stack gap="1">
                                <Divider label={TEXTS.startedHere(formatTime(session.startedHere))} spacing="1" />
                                <Text size="text-xs" color="text-muted" center>
                                    {TEXTS.startedHereNote(helperName)}
                                </Text>
                            </Stack>
                        )}
                        {session.history.length === 0 && session.startedHere == null && (
                            <Text size="text-sm" color="text-muted">
                                {session.student != null ? TEXTS.empty(session.student.firstname) : TEXTS.emptyClass}
                            </Text>
                        )}
                        {session.history.map((entry) =>
                            entry.kind === "since" ? (
                                <View
                                    key={entry.id}
                                    onLayout={(event) => {
                                        since.current = { studentId: studentId!, y: event.nativeEvent.layout.y }
                                        settle()
                                    }}
                                >
                                    <TutorSessionEntry entry={entry} helper={helper} />
                                </View>
                            ) : (
                                <TutorSessionEntry
                                    key={entry.id}
                                    entry={entry}
                                    helper={helper}
                                    onResult={onResult}
                                    onGeneration={onGeneration}
                                    onPeek={onPeek}
                                    // Seule la carte « prêtes » lit l'aperçu. Les lignes sont mémoïsées, mais leurs rappels changent
                                    // encore d'identité à chaque rendu du fil : la mémoïsation ne tient pas encore.
                                    peekId={entry.kind === "ready" ? peekId : undefined}
                                />
                            )
                        )}
                    </Stack>
                </ScrollView>
                {active != null && (
                    <Stack gap="2" style={styles.block(active.urgent)}>
                        {tabs.length > 1 ? (
                            <Tabs
                                items={tabs.map((tab) => ({
                                    key: tab.key,
                                    label: tab.label,
                                    count: tab.count,
                                    countTone: tab.urgent ? ("danger" as const) : ("neutral" as const),
                                }))}
                                value={active.key}
                                onChange={(tab) => setBlock({ key: blockState.key, tab })}
                                size="sm"
                                accessibilityLabel={TEXTS.block}
                            />
                        ) : (
                            <Row gap="2" align="center">
                                <Text
                                    size="text-xs"
                                    weight="bold"
                                    color={active.urgent ? "warning-strong" : "text-strong"}
                                >
                                    {active.label}
                                </Text>
                                {active.count > 1 && <Badge text={cappedCount(active.count)} variant="info" />}
                            </Row>
                        )}
                        {blockOpen ? (
                            <ScrollView
                                style={styles.blockOpen}
                                keyboardShouldPersistTaps="handled"
                                {...dsScrollProps()}
                            >
                                <Stack gap="2">{blockItems}</Stack>
                            </ScrollView>
                        ) : (
                            blockItems
                        )}
                        {(hidden > 0 || blockOpen) && (
                            // Au bord du bloc, comme ce qu'il déplie : centré, il ne se rattachait à rien.
                            <View style={styles.start}>
                                <TextButton
                                    label={blockOpen ? TEXTS.trayLess : TEXTS.trayMore(hidden)}
                                    size="sm"
                                    onPress={() =>
                                        setBlock(
                                            blockOpen
                                                ? { ...blockState, open: undefined }
                                                : { ...blockState, tab: active.key, open: active.key }
                                        )
                                    }
                                />
                            </View>
                        )}
                    </Stack>
                )}
                {composer != null && (
                    <View style={styles.composer}>
                        <TutorDraftComposer composer={composer} />
                    </View>
                )}
            </View>
        </View>
    )
}

/** Un geste de l'en-tête de la colonne : une icône discrète. */
function HeadButton(props: { iconName: IconName; label: string; onPress: () => void; iconSize?: number }) {
    return (
        <IconButton
            iconName={props.iconName}
            appearance="transparent"
            iconColor="text-muted"
            size={32}
            iconSize={props.iconSize ?? 16}
            onPress={props.onPress}
            accessibilityLabel={props.label}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    head: {
        minHeight: 44,
        paddingHorizontal: theme.spacing["1"],
        paddingVertical: theme.spacing["1"],
        marginBottom: theme.spacing["2"],
        overflow: "hidden" as const,
    },
    column: { flex: 1, minHeight: 0 },
    /** Le ruban de la séance, entre deux filets, sous l'en-tête. */
    ribbon: {
        paddingHorizontal: theme.spacing["1"],
        paddingVertical: theme.spacing["3"],
        marginBottom: theme.spacing["2"],
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: theme.colors["border-subtle"],
    },
    scroll: { paddingHorizontal: theme.spacing["1"], paddingBottom: theme.spacing["3"] },
    /**
     * Le bloc du bas, « Ailleurs » et « À décider » : replié sur peu de hauteur, l'historique garde la sienne.
     * Il cède sa hauteur avant le brouillon : sur une tablette, clavier ouvert, la colonne n'a plus que quelques
     * centaines de points et le champ du brouillon passait sous le bas de l'écran (vu sur Android).
     */
    block: (urgent: boolean) => ({
        flexShrink: 1,
        minHeight: 0,
        overflow: "hidden" as const,
        paddingTop: theme.spacing["2"],
        paddingHorizontal: theme.spacing["1"],
        borderTopWidth: urgent ? 2 : 1,
        borderTopColor: urgent ? theme.colors.warning : theme.colors["border-subtle"],
    }),
    blockOpen: { maxHeight: TRAY_OPEN_MAX },
    composer: { flexShrink: 0, paddingTop: theme.spacing["2"], paddingHorizontal: theme.spacing["1"] },
    start: { alignItems: "flex-start" as const },
    flex: { flex: 1, minWidth: 0 },
    wrapRow: { flexWrap: "wrap" as const },
    flexFill: { flex: 1, minHeight: 0 },
}))
